import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Typography } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import styles from './TransactionEditDialog.module.css';
import PropTypes from 'prop-types';
import MenuItem from '@mui/material/MenuItem';

const validationSchema = Yup.object({
    tid: Yup.string().required('Transaction ID is required'),
    rrn: Yup.string().required('RRN is required'),
    transaction_type: Yup.string().required('Transaction type is required'),
    amount: Yup.number().typeError('Must be a number').required('Amount is required'),
    charges: Yup.number().typeError('Must be a number').required('Charges are required'),
    net_amount: Yup.number().typeError('Must be a number').required('Net amount is required'),
    client_id: Yup.string().required('Client is required'),
    service_provider_id: Yup.string().required('Provider name is required')
});

const EditTransactionDialog = ({ open, onClose, onSubmit, initialValues, clientsList, providerList, isDelete, deleteData }) => {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle className={styles.dialogTitle}>{isDelete ? 'Delete' : 'Edit'} Transaction</DialogTitle>
            {isDelete ? (
                <>
                    <DialogContent dividers className={styles.dialogContent}>
                        <Typography className={styles.message}>Are you sure you want to delete this transaction?</Typography>
                        <div className={styles.details}>
                            <div className={styles.detailRow}>
                                <span className={styles.label}>RRN:</span> <span className={styles.value}>{deleteData.rrn}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.label}>TID:</span> <span className={styles.value}>{deleteData.tid}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.label}>Amount:</span> <span className={styles.value}>₹ {deleteData.amount}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.label}>Client Name:</span>{' '}
                                <span className={styles.value}>{deleteData.client_name}</span>
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions className={styles.dialogActions}>
                        <Button onClick={onClose} className={styles.cancelButton}>
                            Cancel
                        </Button>
                        <Button onClick={() => onSubmit(deleteData, 'delete')} variant="contained" className={styles.deleteSubmitButton}>
                            Confirm Delete
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values, actions) => {
                        onSubmit(values, 'edit');
                        actions.setSubmitting(false);
                    }}
                >
                    {({ errors, touched, handleChange, values }) => (
                        <Form>
                            <DialogContent dividers className={styles.dialogContent}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="tid"
                                            label="Transaction ID"
                                            value={values.tid}
                                            onChange={handleChange}
                                            error={touched.tid && Boolean(errors.tid)}
                                            helperText={touched.tid && errors.tid}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="rrn"
                                            label="RRN"
                                            value={values.rrn}
                                            onChange={handleChange}
                                            error={touched.rrn && Boolean(errors.rrn)}
                                            helperText={touched.rrn && errors.rrn}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            name="client_id"
                                            label="Client Name"
                                            value={values.client_id}
                                            onChange={handleChange}
                                            error={touched.client_id && Boolean(errors.client_id)}
                                            helperText={touched.client_id && errors.client_id}
                                            fullWidth
                                        >
                                            {clientsList?.map((client) => (
                                                <MenuItem key={client.id} value={client.id}>
                                                    {client.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            name="service_provider_id"
                                            label="Provider Name"
                                            value={values.service_provider_id}
                                            onChange={handleChange}
                                            error={touched.service_provider_id && Boolean(errors.service_provider_id)}
                                            helperText={touched.service_provider_id && errors.service_provider_id}
                                            fullWidth
                                        >
                                            {providerList?.map((provider) => (
                                                <MenuItem key={provider.id} value={provider.id}>
                                                    {provider.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="transaction_type"
                                            label="Transaction Type"
                                            value={values.transaction_type}
                                            onChange={handleChange}
                                            error={touched.transaction_type && Boolean(errors.transaction_type)}
                                            helperText={touched.transaction_type && errors.transaction_type}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="amount"
                                            label="Amount"
                                            value={values.amount}
                                            onChange={handleChange}
                                            error={touched.amount && Boolean(errors.amount)}
                                            helperText={touched.amount && errors.amount}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="charges"
                                            label="Charges"
                                            value={values.charges}
                                            onChange={handleChange}
                                            error={touched.charges && Boolean(errors.charges)}
                                            helperText={touched.charges && errors.charges}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="net_amount"
                                            label="Net Amount"
                                            value={values.net_amount}
                                            onChange={handleChange}
                                            error={touched.net_amount && Boolean(errors.net_amount)}
                                            helperText={touched.net_amount && errors.net_amount}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions className={styles.dialogActions}>
                                <Button onClick={onClose} className={styles.cancelButton}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="contained" className={styles.submitButton}>
                                    Save Changes
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            )}
        </Dialog>
    );
};

export default EditTransactionDialog;
EditTransactionDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    initialValues: PropTypes.object,
    clientsList: PropTypes.array,
    providerList: PropTypes.array,
    isDelete: PropTypes.string,
    deleteData: PropTypes.object
};
